import type {Optional} from '@yandex-delivery/types'
import {applySpec, constant, equals, find, flow, get, ifElse, merge, toUndefined} from '@yandex-delivery/utils'
import {stubFalse} from 'lodash'

import type {
  SddClaimRoutePoint,
  SearchedSddClaim,
  SearchedSddClaimRoutePoint,
  SearchedSddClaimRoutePoints,
  SourcePoint,
} from 'src/shared/types'
import {generateUniqueId, toPointAddress, toPointContact} from 'src/shared/utils'

// --- Type Helpers ---
const toSourcePointType: () => 'source' = constant('source')
const toSourcePointDeliveryMethod: (isCardClient?: boolean) => 'station' | 'dropOffPoint' = ifElse(
  equals(true),
  constant('dropOffPoint'),
  constant('station'),
)

// --- Route Point Getters ---
const getRoutePointId: (routePoint?: SearchedSddClaimRoutePoint) => Optional<number> = get('id')
const getRoutePointExternalOrderId: (routePoint: SddClaimRoutePoint) => Optional<string> = get('external_order_id')
const getRoutePoints: (sddClaim: SearchedSddClaim) => SearchedSddClaimRoutePoints = get('route_points')

// --- Route Point Checkers ---
const isSourcePoint: (routePoint: SearchedSddClaimRoutePoint) => boolean = flow(get('type'), equals('source'))

// --- Route Point Finders ---
const findSourceRoutePoint: (routePoints: SearchedSddClaimRoutePoints) => Optional<SearchedSddClaimRoutePoint> =
  find(isSourcePoint)

const findSourcePoint: (sddClaim: SearchedSddClaim) => Optional<SearchedSddClaimRoutePoint> = flow(
  getRoutePoints,
  findSourceRoutePoint,
)

// --- Route Point Transformers ---
const baseSourcePointSpec = {
  _type: toSourcePointType,
  externalID: getRoutePointExternalOrderId,
  cashOnDelivery: stubFalse,
  address: toPointAddress,
  station: toUndefined,
  dropOffPoint: toUndefined,
  contact: toPointContact,
  paymentOnDelivery: flow(get('payment_on_delivery'), Boolean),
}

const toFilledSourcePoint: (routePoint?: SearchedSddClaimRoutePoint) => SourcePoint = applySpec<SourcePoint>({
  ...baseSourcePointSpec,
  _id: getRoutePointId,
})

const toInitialSourcePoint: (routePoint?: SearchedSddClaimRoutePoint) => SourcePoint = applySpec<SourcePoint>({
  ...baseSourcePointSpec,
  _id: generateUniqueId,
})

const _toFilledSourcePoint: (sddClaim: SearchedSddClaim) => SourcePoint = flow(findSourcePoint, toFilledSourcePoint)

// --- Main Transformation Functions ---
export const toSourcePoint = (sddClaim?: SearchedSddClaim, isCardClient = false): SourcePoint => {
  const sourcePoint = sddClaim ? _toFilledSourcePoint(sddClaim) : toInitialSourcePoint(undefined)

  return merge(sourcePoint, {
    _deliveryMethod: toSourcePointDeliveryMethod(isCardClient),
  })
}
